import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import useInterval from 'hooks/useInterval';
import { PlayStatus, TraceHistoryMap } from '../types';

interface Props {
  traceHistory: TraceHistoryMap | null;
  globalTraceIndex: number;
  smallestTraceHistoryLength: number;
  setGlobalTraceIndex: Dispatch<SetStateAction<number>>;
  setLocalTraceIndex: Dispatch<SetStateAction<{ 1: number; 2: number }>>;
}

export const usePlaybackBar = ({
  traceHistory,
  globalTraceIndex,
  smallestTraceHistoryLength,
  setGlobalTraceIndex,
  setLocalTraceIndex,
}: Props) => {
  const [playTimerInterval, setPlayTimerInterval] = useState(1000);
  const [playTimerStatus, setPlayTimerStatus] = useState<PlayStatus>(
    PlayStatus.ForwardPaused
  );

  const handlePlayClicked = () => {
    if (traceHistory && globalTraceIndex < smallestTraceHistoryLength - 1)
      setPlayTimerStatus(PlayStatus.Forward);
  };

  const handlePauseClicked = () => {
    setPlayTimerStatus(PlayStatus.ForwardPaused);
  };

  const handleForwardClicked = () => {
    if (traceHistory && globalTraceIndex < smallestTraceHistoryLength - 1) {
      setGlobalTraceIndex(globalTraceIndex + 1);
      setPlayTimerStatus(PlayStatus.ForwardPaused);
    } else return;
  };

  const handleBackwardClicked = () => {
    if (globalTraceIndex > 0) {
      setGlobalTraceIndex(globalTraceIndex - 1);
      setPlayTimerStatus(PlayStatus.ForwardPaused);
    } else return;
  };

  useInterval(
    () => {
      setGlobalTraceIndex((prevState: number) => {
        if (playTimerStatus === PlayStatus.Forward) return prevState + 1;
        return prevState - 1;
      });
    },
    playTimerStatus !== PlayStatus.ForwardPaused ? playTimerInterval : null
  );

  useEffect(() => {
    if (
      (PlayStatus.Forward &&
        globalTraceIndex === smallestTraceHistoryLength - 1) ||
      (PlayStatus.Backward && globalTraceIndex === 0)
    ) {
      setPlayTimerStatus(PlayStatus.ForwardPaused);
    }
    setLocalTraceIndex((prevState: { 1: number; 2: number }) => ({
      ...prevState,
      1: globalTraceIndex,
      2: globalTraceIndex,
    }));
    //eslint-disable-next-line
  }, [globalTraceIndex, smallestTraceHistoryLength]);

  return {
    handlePlayClicked,
    handlePauseClicked,
    handleForwardClicked,
    handleBackwardClicked,
    playTimerInterval,
    setPlayTimerInterval,
    playTimerStatus,
  } as const;
};

export default usePlaybackBar;
